import styled, { css } from "styled-components";
import { Space } from "@ardoq/style-helpers";
import { s32, s24, s16, s8, colors, r8, s4, zIndex, backgroundTokens } from "@ardoq/design-tokens";
import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { ICON_SELECTOR, Icon, IconSize, IconName, AnimatedChevron, DragButton } from "@ardoq/icons";
import { Tag, StatusType, Notification, NOTIFICATION_SELECTOR } from "@ardoq/status-ui";
import { withPlainTextPopover, TextOverflow } from "@ardoq/popovers";
import { header2, header3, caption, normal14, text2 } from "@ardoq/typography";
import { FlexBox } from "@ardoq/layout";
import { GhostButton, ButtonGroup, BUTTON_SELECTOR, PrimaryButton } from "@ardoq/button";
import { useState, useRef, useLayoutEffect, forwardRef } from "react";
import { DragAndDropCardWrapper } from "@ardoq/drag-and-drop";
import { markdownToTextPreview, MarkdownViewer } from "@ardoq/markdown";
import { ResizableHorizontal, HorizontalResizeableContainerWrapper } from "@ardoq/resizable";
const SPACING_LOOKUP = {
  none: "0",
  small: s8,
  medium: s16,
  large: s24,
  xlarge: s32
};
const IslandSectionContainer = styled(Space).attrs({
  $isVertical: true,
  $gap: "none"
})`
  padding: ${({ $spacing = "xlarge" }) => SPACING_LOOKUP[$spacing]};
  border-bottom: 1px solid ${colors.borderAction};
  &:only-child {
    border-bottom: 0;
  }
  &:last-child {
    border-bottom: none;
  }
`;
const IslandBody = styled(IslandSectionContainer).attrs({
  $gap: "s16"
})`
  ${({ $isScrollable, $preventScroll }) => $isScrollable && css`
        max-height: 100%;
        overflow-y: scroll;
      ` || $preventScroll && css`
        max-height: 100%;
        overflow-y: hidden;
      `}

  ${({ $fullHeight }) => $fullHeight && css`
      flex-grow: 1;
    `}
`;
const ISLAND_DEFAULT_MAX_WIDTH = "1440px";
const ISLAND_DEFAULT_MIN_WIDTH = "700px";
const IslandContainer = styled(Space).attrs({
  $isVertical: true,
  $gap: "none"
})`
  border-radius: ${r8};
  background-color: ${colors.surfaceDefault};
  min-width: ${({ $minWidth = ISLAND_DEFAULT_MIN_WIDTH }) => $minWidth};
  max-width: ${({ $maxWidth = ISLAND_DEFAULT_MAX_WIDTH }) => $maxWidth};
  ${({ $withBorder = true }) => $withBorder && css`
      border: 1px solid ${colors.borderAction};
    `};
  ${({ $minHeight }) => $minHeight && css`
      min-height: ${$minHeight};
    `};
  ${({ $isSelectable }) => $isSelectable && css`
      &:hover {
        outline: 1px solid ${colors.borderActionHover};
        cursor: pointer;
      }
    `}
  ${({ $isSelected }) => $isSelected && css`
      outline: 1px solid ${colors.borderActionActive};
    `}
  ${({ $preventScroll }) => $preventScroll && css`
      overflow-y: hidden;
    `}
  ${({ $fullHeight }) => $fullHeight && css`
      min-height: 100%;
      overflow-y: auto;
    `}
`;
const ISLAND_SELECTOR = IslandContainer.toString();
const IslandFooter = styled(IslandSectionContainer).attrs({
  $spacing: "medium"
})`
  align-items: center;
  flex-direction: row;
`;
const IslandHeaderBody = styled.div`
  flex: 1;
`;
const IslandHeaderTitle = styled.div`
  ${header2};
  display: flex;
  align-items: center;

  ${(props) => !props.$preventIconChange && css`
      ${ICON_SELECTOR} {
        color: ${colors.icon};
        &:first-child {
          margin-right: ${s8};
        }
        &:last-child {
          margin-left: ${s4};
        }
      }
    `}
`;
const IslandHeaderTitlePlaceholder = styled.div`
  ${header2};
  user-select: none;
  color: ${colors.textSubtle};
`;
const IslandSubheaderTitle = styled.div`
  ${header3};
  color: ${colors.textModerate};
`;
const tokenToWidth = {
  default: "540px",
  medium: "720px",
  large: "1020px"
};
const IslandHeaderSubtitle = styled.div`
  ${caption};
  color: ${colors.textSubtle};
  max-width: ${({ $subtitleWidth = "default" }) => tokenToWidth[$subtitleWidth]};
`;
const IslandHeaderTagsWrapper = styled(Space).attrs({ $gap: "s8" })`
  margin-bottom: ${s16};
  &:empty {
    display: none;
  }
`;
const IslandHeaderWrapper = styled(IslandSectionContainer).attrs({
  $isVertical: false,
  $align: "center"
})`
  display: block;
  width: 100%;
  padding: ${({ $skipVerticalPadding, $spacing = "medium" }) => $skipVerticalPadding ? `0 ${SPACING_LOOKUP[$spacing]}` : SPACING_LOOKUP[$spacing]};
  ${(props) => props.$hasColorBar && css`
      padding-left: ${s8};
      border-bottom: 0;
    `}
  ${IslandHeaderSubtitle} {
    margin-top: ${s4};
  }
`;
const IslandSubheaderWrapper = styled(Space).attrs({ $align: "center" })`
  ${IslandHeaderSubtitle} {
    margin-top: ${s8};
  }
`;
const ColorBar = styled.div.attrs(
  ({ $barColor }) => ({
    style: { background: $barColor }
  })
)`
  width: 4px;
  border-radius: 8px;
  margin: ${s16} 0 ${s16} ${s16};
  float: left;
`;
const COLOR_BAR_SELECTOR = ColorBar.toString();
const BorderWrapper$1 = styled.div`
  display: flex;
  ${({ $showHeaderBorder }) => $showHeaderBorder && `border-bottom: 1px solid ${colors.borderAction};`}
  &:only-child {
    border-bottom: 0;
  }
  cursor: ${({ $clickable }) => $clickable ? "pointer" : "default"};
  &:hover {
    background-color: ${({ $clickable }) => $clickable ? colors.surfaceActionGhostHover : "none"};
  }
`;
const IslandHeader = ({
  title,
  titlePlaceholder,
  titleAddon,
  subtitle,
  subtitleWidth,
  tags,
  errorMessage,
  warningMessage,
  iconName,
  knowledgeHint,
  rightContent,
  children,
  skipVerticalPadding,
  skipVerticalAlign,
  barColor,
  knowledgeBaseLink,
  showHeaderBorder = true,
  headerSpacing,
  preventTitleIconChange,
  onHeaderClicked
}) => {
  const shouldRenderHeader = Boolean(
    title || titlePlaceholder || subtitle || tags || errorMessage || children
  );
  if (!shouldRenderHeader) {
    return null;
  }
  const clickable = Boolean(onHeaderClicked);
  return /* @__PURE__ */ jsxs(
    BorderWrapper$1,
    {
      $clickable: clickable,
      onClick: onHeaderClicked,
      onKeyDown: (e) => {
        if (e.key === " " || e.key === "Enter" || e.key === "Spacebar") {
          onHeaderClicked == null ? void 0 : onHeaderClicked(e);
        }
      },
      "data-test-id": "collapsible-island-header",
      tabIndex: clickable ? 0 : void 0,
      "aria-pressed": clickable ? "false" : void 0,
      $showHeaderBorder: showHeaderBorder,
      children: [
        barColor && /* @__PURE__ */ jsx(ColorBar, { $barColor: barColor }),
        /* @__PURE__ */ jsxs(
          IslandHeaderWrapper,
          {
            $skipVerticalPadding: skipVerticalPadding,
            $hasColorBar: Boolean(barColor),
            $spacing: headerSpacing,
            children: [
              /* @__PURE__ */ jsxs(
                FlexBox,
                {
                  gap: "medium",
                  ...skipVerticalAlign ? {} : { align: "center" },
                  children: [
                    /* @__PURE__ */ jsxs(IslandHeaderBody, { children: [
                      /* @__PURE__ */ jsxs(IslandHeaderTagsWrapper, { children: [
                        errorMessage ? /* @__PURE__ */ jsx(Tag, { statusType: StatusType.ERROR, children: errorMessage }) : warningMessage ? /* @__PURE__ */ jsx(Tag, { statusType: StatusType.WARNING, children: warningMessage }) : null,
                        tags
                      ] }),
                      !title && !!titlePlaceholder && /* @__PURE__ */ jsx(IslandHeaderTitlePlaceholder, { children: titlePlaceholder }),
                      title && /* @__PURE__ */ jsxs(FlexBox, { gap: "xsmall", children: [
                        /* @__PURE__ */ jsxs(IslandHeaderTitle, { $preventIconChange: preventTitleIconChange, children: [
                          iconName && /* @__PURE__ */ jsx(Icon, { iconName }),
                          /* @__PURE__ */ jsx("span", { children: typeof title === "string" && !title.trim() ? "\0" : title }),
                          (knowledgeBaseLink || knowledgeHint) && /* @__PURE__ */ jsx(
                            Icon,
                            {
                              iconName: IconName.KNOWLEDGE_BASE,
                              iconSize: IconSize.MEDIUM,
                              ...!knowledgeHint ? { "data-tooltip-text": "Knowledge base" } : {},
                              ...withPlainTextPopover(knowledgeHint),
                              onClick: () => knowledgeBaseLink && window.open(knowledgeBaseLink, "_blank noreferrer"),
                              style: {
                                cursor: "pointer"
                              }
                            }
                          )
                        ] }),
                        titleAddon
                      ] }),
                      subtitle && /* @__PURE__ */ jsx(IslandHeaderSubtitle, { $subtitleWidth: subtitleWidth, children: subtitle })
                    ] }),
                    rightContent
                  ]
                }
              ),
              children
            ]
          }
        )
      ]
    }
  );
};
const IslandSubheader = ({
  title,
  subtitle,
  knowledgeHint
}) => /* @__PURE__ */ jsx(IslandSubheaderWrapper, { children: /* @__PURE__ */ jsxs(IslandHeaderBody, { children: [
  title && /* @__PURE__ */ jsxs(IslandSubheaderTitle, { children: [
    /* @__PURE__ */ jsx("span", { children: title }),
    knowledgeHint && /* @__PURE__ */ jsx(
      Icon,
      {
        iconName: IconName.KNOWLEDGE_BASE,
        iconSize: IconSize.MEDIUM,
        ...withPlainTextPopover(knowledgeHint)
      }
    )
  ] }),
  subtitle && /* @__PURE__ */ jsx(IslandHeaderSubtitle, { children: subtitle })
] }) });
const Island = ({
  children,
  bodySpacing,
  preventScroll,
  isScrollable,
  footerContent,
  footerJustify,
  header,
  isSelected,
  isSelectable,
  onClick,
  maxWidth,
  minWidth,
  minHeight,
  withBorder = true,
  fullHeight = false,
  flex,
  ...headerProps
}) => /* @__PURE__ */ jsxs(
  IslandContainer,
  {
    $isSelectable: isSelectable,
    $isSelected: isSelected,
    onClick,
    $maxWidth: maxWidth,
    $minWidth: minWidth,
    $minHeight: minHeight,
    $preventScroll: preventScroll,
    $withBorder: withBorder,
    $fullHeight: fullHeight,
    $flex: flex,
    children: [
      header || /* @__PURE__ */ jsx(IslandHeader, { ...headerProps }),
      children && /* @__PURE__ */ jsx(
        IslandBody,
        {
          $fullHeight: fullHeight,
          $flex: flex,
          $spacing: bodySpacing,
          $preventScroll: preventScroll,
          $isScrollable: isScrollable,
          children
        }
      ),
      footerContent && /* @__PURE__ */ jsx(IslandFooter, { $justify: footerJustify, children: footerContent })
    ]
  }
);
const CollapsibleIsland = ({
  isDefaultExpanded,
  onToggle,
  toggleOnHeaderClick = true,
  ...islandProps
}) => {
  const [isExpanded, setIsExpanded] = useState(isDefaultExpanded ?? false);
  const toggleExpand = () => {
    onToggle == null ? void 0 : onToggle(!isExpanded);
    setIsExpanded(!isExpanded);
  };
  const islandMinHeight = isExpanded ? islandProps.minHeight : islandProps.collapsedMinHeight ?? islandProps.minHeight;
  return /* @__PURE__ */ jsx(
    ControlledCollapsibleIsland,
    {
      ...islandProps,
      minHeight: islandMinHeight,
      isExpanded,
      onToggle: toggleExpand,
      toggleOnHeaderClick
    }
  );
};
const ControlledCollapsibleIsland = ({
  isExpanded,
  onToggle,
  children,
  rightContent,
  toggleOnHeaderClick = true,
  ...islandProps
}) => {
  const toggleExpand = (event) => {
    event.stopPropagation();
    event.preventDefault();
    onToggle == null ? void 0 : onToggle(!isExpanded);
  };
  return /* @__PURE__ */ jsx(
    Island,
    {
      ...islandProps,
      onHeaderClicked: toggleOnHeaderClick ? toggleExpand : void 0,
      rightContent: /* @__PURE__ */ jsxs(Fragment, { children: [
        rightContent,
        /* @__PURE__ */ jsx(
          GhostButton,
          {
            onClick: toggleExpand,
            "data-tooltip-text": isExpanded ? "Collapse" : "Expand",
            children: /* @__PURE__ */ jsx(AnimatedChevron, { $isUp: isExpanded })
          }
        )
      ] }),
      children: isExpanded && children
    }
  );
};
const IslandDragAndDropCardWrapper = styled(DragAndDropCardWrapper)`
  flex: 1;
  align-items: center;
  width: 100%;
  ${DragButton} {
    padding-left: ${s4};
  }
`;
const BorderWrapper = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.borderAction};
  &:only-child {
    border-bottom: 0;
  }
`;
const DragAndDropIslandHeader = ({
  barColor,
  dragAndDropCardId,
  ...headerProps
}) => /* @__PURE__ */ jsxs(BorderWrapper, { children: [
  barColor && /* @__PURE__ */ jsx(ColorBar, { $barColor: barColor }),
  /* @__PURE__ */ jsx(
    IslandDragAndDropCardWrapper,
    {
      dragAndDropCardId,
      dragButtonAlwaysVisible: true,
      dragButtonPosition: "left",
      children: /* @__PURE__ */ jsx(IslandHeader, { ...headerProps })
    }
  )
] });
const Expander = styled.button`
  color: ${colors.textAction};
  ${normal14};
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
  border: 0;
  background: ${colors.surfaceDefault};
  text-align: left;
  width: fit-content;
  cursor: pointer;
  &:hover,
  &:focus {
    color: ${colors.textActionHover};
  }
`;
const ErrorNotification = styled(Notification)`
  margin: ${s16} 0px;
`;
const MarkdownPreviewWrapper = styled.div`
  display: flex;
  gap: 12px;
`;
const MarkdownPreview = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const DescriptionPlaceholder = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
  color: ${colors.textSubtle};
`;
const IslandActionsButtonGroup = styled(ButtonGroup)`
  align-self: flex-start;
`;
const PageHeaderContainer = styled.div`
  ${({ $showButtonsOnlyOnHover }) => $showButtonsOnlyOnHover && css`
      ${IslandActionsButtonGroup} ${BUTTON_SELECTOR} {
        visibility: hidden;
      }

      &:hover ${IslandActionsButtonGroup} ${BUTTON_SELECTOR} {
        visibility: visible;
      }
    `}
`;
const PageHeaderIsland = ({
  description,
  descriptionPlaceholder,
  buttons,
  errorMessage,
  maxWidth,
  showButtonsOnlyOnHover = false,
  trackEventExpandDescription,
  showExpandDescriptionButton,
  ...props
}) => {
  const [descriptionPreview, setDescriptionPreview] = useState();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [enableShowMore, setEnableShowMore] = useState(false);
  const previewRef = useRef(null);
  useLayoutEffect(() => {
    const preview = description && markdownToTextPreview(description);
    const descriptionNormalized = description && description.replace(/\.\.\./g, "…");
    const hasOverflow = !!previewRef.current && previewRef.current.scrollWidth >= previewRef.current.parentElement.offsetWidth;
    if (preview) setDescriptionPreview(preview);
    setEnableShowMore(
      !isDescriptionExpanded && preview === descriptionPreview && (descriptionNormalized !== preview || hasOverflow)
    );
  }, [description, descriptionPreview, isDescriptionExpanded]);
  return /* @__PURE__ */ jsx(PageHeaderContainer, { $showButtonsOnlyOnHover: showButtonsOnlyOnHover, children: /* @__PURE__ */ jsx(
    Island,
    {
      maxWidth,
      subtitleWidth: "large",
      subtitle: /* @__PURE__ */ jsxs(Fragment, { children: [
        errorMessage && /* @__PURE__ */ jsx(ErrorNotification, { error: true, children: errorMessage }),
        !description && !!descriptionPlaceholder && /* @__PURE__ */ jsx(DescriptionPlaceholder, { children: descriptionPlaceholder }),
        description && !isDescriptionExpanded && /* @__PURE__ */ jsxs(MarkdownPreviewWrapper, { children: [
          /* @__PURE__ */ jsx(MarkdownPreview, { ref: previewRef, children: descriptionPreview }),
          showExpandDescriptionButton && enableShowMore && /* @__PURE__ */ jsx(
            Expander,
            {
              onClick: () => {
                trackEventExpandDescription == null ? void 0 : trackEventExpandDescription(true);
                setIsDescriptionExpanded(true);
              },
              children: "Show more"
            }
          )
        ] }),
        description && isDescriptionExpanded && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            MarkdownViewer,
            {
              content: description ?? "",
              sanitationLevel: "permissive"
            }
          ),
          showExpandDescriptionButton && /* @__PURE__ */ jsx(
            Expander,
            {
              onClick: () => {
                trackEventExpandDescription == null ? void 0 : trackEventExpandDescription(true);
                setIsDescriptionExpanded(false);
              },
              children: "Show less"
            }
          )
        ] })
      ] }),
      rightContent: buttons ? /* @__PURE__ */ jsx(IslandActionsButtonGroup, { children: buttons }) : null,
      ...props
    }
  ) });
};
const PageHeaderWrapper = styled(Space).attrs(({ $align = "start", $justify = "space-between", $gap }) => ({
  $align,
  $justify,
  $gap
}))`
  /* We want to make the header constant height
     regardless we have or not the header buttons
     52px = 36px of button + 2 x 8px padding */
  min-height: 52px;
  border-bottom: 1px solid ${colors.borderAction};
  background-color: ${colors.surfaceDefault};
  padding: ${({ $skipVerticalPadding }) => $skipVerticalPadding ? `0 ${s16}` : `${s8} ${s32}`};
  z-index: ${zIndex.PAGE_HEADER};
`;
const TitleWrapper = styled.div`
  ${header2};
  color: ${colors.textDefault};
  display: flex;
  /* button height */
  align-items: center;
  min-height: 36px;
`;
const PageHeader = ({
  title,
  leftContent,
  rightContent,
  children,
  skipVerticalPadding,
  ...spaceProps
}) => {
  return /* @__PURE__ */ jsxs(
    PageHeaderWrapper,
    {
      $skipVerticalPadding: skipVerticalPadding,
      ...spaceProps,
      children: [
        (leftContent || title) && /* @__PURE__ */ jsxs(FlexBox, { gap: "small", children: [
          title && /* @__PURE__ */ jsx(TitleWrapper, { children: title }),
          leftContent
        ] }),
        rightContent && /* @__PURE__ */ jsx("div", { children: rightContent }),
        children
      ]
    }
  );
};
const PageWrapper = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  flex-direction: column;
  height: 100%;
`;
const PageBodyWrapper = styled.div`
  height: 100%;
  background-color: ${({ $backgroundColor }) => $backgroundColor ? colors[$backgroundColor] : colors.surfaceStatusNeutral};
  background-image: ${({ $backgroundPattern }) => $backgroundPattern === "dotted" && backgroundTokens.dotted};
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${s16};

  ${({ $alignIslandsToLeft }) => !$alignIslandsToLeft && css`
      ${ISLAND_SELECTOR}, ${NOTIFICATION_SELECTOR} {
        margin: 0 auto;
        width: 100%;
      }
    `}

  ${({ $skipPadding, $padding = s32 }) => !$skipPadding && css`
      padding: ${$padding};
    `}

  ${({ $preventScroll }) => $preventScroll ? css`
          overflow-y: hidden;

          & > ${ISLAND_SELECTOR} {
            max-height: 100%;
          }
        ` : css`
          overflow-y: auto;
        `}
`;
const PageBodyOuterWrapper$1 = styled(Space)`
  gap: 0;
  overflow: hidden;
  flex: 1;
`;
const PageBody = ({
  children,
  leftContent,
  rightContent,
  headerContent,
  preventScroll,
  alignIslandsToLeft,
  backgroundColor,
  backgroundPattern,
  footerContent,
  skipPadding,
  padding
}) => /* @__PURE__ */ jsxs(PageBodyOuterWrapperForFooter, { children: [
  /* @__PURE__ */ jsxs(PageBodyOuterWrapper$1, { children: [
    leftContent,
    /* @__PURE__ */ jsxs(FlexBox, { flexDirection: "column", flex: 1, style: { overflowY: "auto" }, children: [
      headerContent,
      /* @__PURE__ */ jsx(
        PageBodyWrapper,
        {
          $preventScroll: preventScroll,
          $alignIslandsToLeft: alignIslandsToLeft,
          $backgroundColor: backgroundColor,
          $backgroundPattern: backgroundPattern,
          $skipPadding: skipPadding,
          $padding: padding,
          children
        }
      )
    ] }),
    rightContent
  ] }),
  footerContent
] });
const PageBodyOuterWrapperForFooter = styled(PageBodyOuterWrapper$1)`
  flex-direction: column;
`;
const PageBodyOuterWrapper = styled(Space)`
  gap: 0;
  overflow: hidden;
  flex: 1;
`;
const SIDE_CONTAINER_MIN_WIDTH = 255;
const SideContainer = styled(Space)`
  height: 100%;
`;
const ResizablePageBody = forwardRef(
  ({
    children,
    leftContent,
    leftContentInitialWidth = SIDE_CONTAINER_MIN_WIDTH,
    leftContentMinWidth = SIDE_CONTAINER_MIN_WIDTH,
    rightContent,
    rightContentInitialWidth = SIDE_CONTAINER_MIN_WIDTH,
    rightContentMinWidth = SIDE_CONTAINER_MIN_WIDTH,
    headerContent,
    preventScroll,
    backgroundColor,
    skipPadding,
    footerContent
  }, ref) => /* @__PURE__ */ jsxs(PageBodyOuterWrapperForFooter, { ref, children: [
    /* @__PURE__ */ jsx(PageBodyOuterWrapper, { children: /* @__PURE__ */ jsxs(ResizableHorizontal, { hasHorizontalScroll: true, children: [
      leftContent ? /* @__PURE__ */ jsx(
        HorizontalResizeableContainerWrapper,
        {
          minWidth: leftContentMinWidth,
          initialWidth: leftContentInitialWidth,
          children: /* @__PURE__ */ jsx(SideContainer, { children: leftContent })
        },
        "leftSide"
      ) : /* @__PURE__ */ jsx(
        HorizontalResizeableContainerWrapper,
        {
          isCollapsed: true,
          initialWidth: 0
        },
        "leftSide"
      ),
      /* @__PURE__ */ jsx(HorizontalResizeableContainerWrapper, { children: /* @__PURE__ */ jsxs(
        FlexBox,
        {
          flexDirection: "column",
          flex: 1,
          height: "full",
          style: { overflowY: "auto" },
          children: [
            headerContent,
            /* @__PURE__ */ jsx(
              PageBodyWrapper,
              {
                $skipPadding: skipPadding,
                $preventScroll: preventScroll,
                $backgroundColor: backgroundColor,
                children
              }
            )
          ]
        },
        "body"
      ) }),
      rightContent ? /* @__PURE__ */ jsx(
        HorizontalResizeableContainerWrapper,
        {
          minWidth: rightContentMinWidth,
          initialWidth: rightContentInitialWidth,
          children: /* @__PURE__ */ jsxs(SideContainer, { children: [
            " ",
            rightContent
          ] })
        },
        "rightSide"
      ) : /* @__PURE__ */ jsx(
        HorizontalResizeableContainerWrapper,
        {
          isCollapsed: true,
          initialWidth: 0
        },
        "rightSide"
      )
    ] }) }),
    footerContent
  ] })
);
const PageBodyFooter = styled(Space)`
  width: 100%;
  padding: ${s16};
  background-color: ${colors.surfaceDefault};
  border-top: 1px solid ${colors.borderSubtle00};
  justify-content: flex-end;
`;
const PAGE_TOPBAR_HEIGHT = 96;
const TopbarWrapper = styled(FlexBox).attrs((props) => ({
  paddingX: "large",
  align: "center",
  justify: "space-between",
  width: "full",
  className: props.className
}))`
  background-color: ${colors.surfaceDefault};
  color: ${colors.textDefault};
  height: ${PAGE_TOPBAR_HEIGHT}px;
`;
const Right = styled(Space).attrs({ $align: "center", $justify: "flex-end" })`
  flex: 1 1 auto;
  margin-left: auto;
`;
const SecondaryText = styled.span`
  ${text2};
  color: ${colors.textSubtle};
`;
const PrimaryText = styled.h2`
  ${header2};
  color: ${colors.textDefault};
  margin: 0;
`;
const ToolbarDivider = styled.hr`
  background-color: ${colors.borderSubtle00};
  height: ${s24};
  width: 1px;
  border-style: none;
`;
const CreateNewButton = (props) => {
  return /* @__PURE__ */ jsxs(PrimaryButton, { ...props, children: [
    "Create new",
    /* @__PURE__ */ jsx(Icon, { iconName: IconName.ADD })
  ] });
};
const PageTopbar = ({
  toolbarContent,
  primaryContent,
  secondaryContent,
  primaryButton,
  secondaryButton,
  middleContent,
  className
}) => {
  const shouldRenderWithTextOverflow = typeof primaryContent === "string";
  return /* @__PURE__ */ jsxs(TopbarWrapper, { role: "banner", className, children: [
    /* @__PURE__ */ jsxs(
      FlexBox,
      {
        flexDirection: "column",
        gap: "xsmall",
        style: { overflow: "hidden" },
        children: [
          /* @__PURE__ */ jsx(SecondaryText, { children: secondaryContent }),
          shouldRenderWithTextOverflow ? /* @__PURE__ */ jsx(TextOverflow, { lineClamp: 1, children: /* @__PURE__ */ jsx(PrimaryText, { children: primaryContent }) }) : /* @__PURE__ */ jsx(PrimaryText, { children: primaryContent })
        ]
      }
    ),
    middleContent,
    /* @__PURE__ */ jsxs(Right, { children: [
      toolbarContent,
      secondaryButton || primaryButton ? /* @__PURE__ */ jsxs(Space, { $gap: "s8", $align: "center", children: [
        secondaryButton,
        primaryButton
      ] }) : null
    ] })
  ] });
};
export {
  COLOR_BAR_SELECTOR,
  CollapsibleIsland,
  ColorBar,
  ControlledCollapsibleIsland,
  CreateNewButton,
  DragAndDropIslandHeader,
  ISLAND_DEFAULT_MAX_WIDTH,
  ISLAND_SELECTOR,
  Island,
  IslandBody,
  IslandContainer,
  IslandFooter,
  IslandHeader,
  IslandHeaderSubtitle,
  IslandSubheader,
  PAGE_TOPBAR_HEIGHT,
  PageBody,
  PageBodyFooter,
  PageBodyOuterWrapperForFooter,
  PageBodyWrapper,
  PageHeader,
  PageHeaderIsland,
  PageTopbar,
  PageWrapper,
  ResizablePageBody,
  ToolbarDivider
};
//# sourceMappingURL=index.js.map
